import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { buildUpdateSessionTeacherPayload } from '@/lib/coach-assignment-utils.js';
import {
  getAdminSession,
  getAdminSessions,
  updateSessionOriginalTeacher,
  updateSessionTeacher,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { stringIfNull } from '@/lib/string-utils.js';
import { isOriginalTeacherPendingSubstitute } from '@/routes/_layout/sessions/-components/original-teacher-display.js';
import { isSessionTeacherPendingSubstitute } from '@/routes/_layout/sessions/-components/session-teacher-display.js';
import {
  AdminSession,
  CoachSessionRemarkV2,
  CoachSessionRemarkV2LabelMap,
  SubstitutionRequestStatus,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// Matches the placeholder coach the Sub sessions filter looks for on the server.
const PLACEHOLDER_SUBSTITUTE_FIRST_NAME = 'Substitute';
const PLACEHOLDER_SUBSTITUTE_LAST_NAME = 'Teacher';

const proposedTeacherSessionRemarkOptions = [
  CoachSessionRemarkV2.subClass,
  CoachSessionRemarkV2.subClassWithin12Hours,
] as const;

const formSchema = z.object({
  sessionRemark: z.enum(proposedTeacherSessionRemarkOptions).optional(),
});

type FormData = z.infer<typeof formSchema>;

type ProposedTeacher = NonNullable<AdminSession['proposedTeacher']>;

/**
 * The Sub sessions list also keeps rows that are only there because "Hold session" is checked,
 * and those have nothing left to approve.
 */
const needsSubstituteTeacher = (session: AdminSession): boolean => {
  const hasPendingRequest = session.substitutionRequests.some(
    (request) => request.status === SubstitutionRequestStatus.pending,
  );
  const hasPlaceholderSubstitute =
    session.substituteCoach?.firstName === PLACEHOLDER_SUBSTITUTE_FIRST_NAME &&
    session.substituteCoach.lastName === PLACEHOLDER_SUBSTITUTE_LAST_NAME;

  return hasPendingRequest || hasPlaceholderSubstitute;
};

const useApproveProposedTeacher = (session: AdminSession, proposedTeacher: ProposedTeacher) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateSessionTeacherMutate, isPending: isAssigningSessionTeacher } = useMutation({
    mutationFn: updateSessionTeacher,
  });
  const { mutateAsync: updateSessionOriginalTeacherMutate, isPending: isAssigningOriginalTeacher } = useMutation({
    mutationFn: updateSessionOriginalTeacher,
  });

  const teacherName = proposedTeacher.fullNameOrEmail ?? 'Teacher';

  const refreshSessionQueries = async (updatedSession?: Awaited<ReturnType<typeof updateSessionTeacher>>) => {
    if (updatedSession) {
      queryClient.setQueryData(getAdminSession(session.id).queryKey, updatedSession);
    }

    await Promise.all([
      queryClient.invalidateQueries(getAdminSessions()),
      queryClient.invalidateQueries(getAdminSession(session.id)),
    ]);
  };

  const approve = async ({
    assignAsOriginalTeacher,
    sessionRemark,
  }: {
    assignAsOriginalTeacher: boolean;
    sessionRemark?: FormData['sessionRemark'];
  }) => {
    const assignTeacher = () => {
      if (assignAsOriginalTeacher) {
        return updateSessionOriginalTeacherMutate({
          sessionId: session.id,
          payload: { coachId: proposedTeacher.id, sessionRemark },
        });
      }

      return updateSessionTeacherMutate({
        sessionId: session.id,
        payload: buildUpdateSessionTeacherPayload(session, {
          sessionTeacherId: proposedTeacher.id,
          originalTeacherClassIssue: stringIfNull(session.originalTeacherIssue),
          reason: session.originalTeacherIssueReason ?? '',
        }),
      });
    };

    try {
      const updatedSession = await assignTeacher();

      await refreshSessionQueries(updatedSession);

      toast({
        title: 'Proposed teacher approved',
        description: assignAsOriginalTeacher
          ? `${teacherName} has been assigned as the original teacher and the session teacher.`
          : `${teacherName} has been assigned to the session.`,
      });
      return true;
    } catch (error) {
      toast({
        title: 'Failed to approve proposed teacher',
        description:
          airServerErrorFrom(error)?.userFacingMessage ??
          (error instanceof Error ? error.message : 'Please try again.'),
        variant: 'destructive',
      });
      return false;
    }
  };

  return { approve, isApproving: isAssigningSessionTeacher || isAssigningOriginalTeacher };
};

const ApproveProposedTeacherModal = ({
  session,
  proposedTeacher,
}: {
  session: AdminSession;
  proposedTeacher: ProposedTeacher;
}) => {
  const { approve } = useApproveProposedTeacher(session, proposedTeacher);

  const onSubmit = async ({ closeModal, formData }: SubmitProps<FormData>) => {
    if (await approve({ assignAsOriginalTeacher: true, sessionRemark: formData.sessionRemark })) {
      closeModal();
    }
  };

  return (
    <FormModal
      modalTitle="Approve proposed teacher"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Confirm and assign"
      cancelBtnLabel="Cancel"
      dataTestId="approve-proposed-teacher-modal"
      trigger={
        <Button size="sm" variant="destructive">
          Approve
        </Button>
      }
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg' }}
    >
      {({ form }) => (
        <div className="space-y-4">
          <p className="text-muted-foreground pt-2">
            Assign <span className="font-semibold">{proposedTeacher.fullNameOrEmail}</span> as both the{' '}
            <span className="font-semibold">Original teacher</span> and{' '}
            <span className="font-semibold">Session teacher</span>.
          </p>
          <FormSingleSelect
            form={form}
            name="sessionRemark"
            label="Session remarks"
            placeholder="Select an option"
            options={proposedTeacherSessionRemarkOptions}
            renderOption={(option) => (option ? CoachSessionRemarkV2LabelMap[option] : '')}
          />
        </div>
      )}
    </FormModal>
  );
};

const ApproveProposedTeacherButton = ({
  session,
  proposedTeacher,
}: {
  session: AdminSession;
  proposedTeacher: ProposedTeacher;
}) => {
  const { approve, isApproving } = useApproveProposedTeacher(session, proposedTeacher);

  return (
    <Button
      size="sm"
      variant="destructive"
      loading={isApproving}
      onClick={() => void approve({ assignAsOriginalTeacher: false })}
    >
      Approve
    </Button>
  );
};

export const ApproveProposedTeacherAction = ({ session }: { session: AdminSession }) => {
  const proposedTeacher = session.proposedTeacher;

  if (!proposedTeacher || !needsSubstituteTeacher(session)) {
    return (
      <Button size="sm" variant="destructive" disabled>
        Approve
      </Button>
    );
  }

  return isOriginalTeacherPendingSubstitute(session) && isSessionTeacherPendingSubstitute(session) ? (
    <ApproveProposedTeacherModal session={session} proposedTeacher={proposedTeacher} />
  ) : (
    <ApproveProposedTeacherButton session={session} proposedTeacher={proposedTeacher} />
  );
};
